import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AvisarWhatsapp } from '../componentes/AvisarWhatsapp.tsx';
import { type PacienteResumo } from '../componentes/BuscaPaciente.tsx';
import { CamposDoExame, exameVazio, type FormExame } from '../componentes/CamposDoExame.tsx';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Aviso, Botao, Chapa, Selo } from '../componentes/forma.tsx';
import { api, ErroApi } from '../lib/api.ts';
import { useOpcoes } from '../lib/cadastro.ts';
import {
  dataBr, dinheiro, hojeIso, paraCampoDinheiro, temDocumentoSemLaudo,
} from '../lib/formato.ts';
import { usePode, useUsuario } from '../lib/sessao.tsx';
import type { ExameLinha } from './Fila.tsx';
import './exameEditor.css';

export function ExameEditor() {
  const { id } = useParams<{ id: string }>();
  const novo = !id || id === 'novo';
  const navegar = useNavigate();
  const qc = useQueryClient();
  const pode = usePode();
  const usuario = useUsuario();
  const podeVerValores = pode('pode_financeiro');
  const podeAlterar = pode('pode_exame_alterar');
  const podeAvisarPaciente = pode('pode_avisar_pacientes');
  const podeExcluirImagem = pode('pode_excluir_imagens');
  const opcoes = useOpcoes();

  const [form, setForm] = useState<FormExame>(() => exameVazio(hojeIso()));
  const [paciente, setPaciente] = useState<PacienteResumo | null>(null);
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState('');

  const exame = useQuery({
    queryKey: ['exame', id],
    queryFn: ({ signal }) => api.get<ExameLinha & Record<string, unknown>>(`/exames/${id}`, signal),
    enabled: !novo,
  });

  // Carrega o exame existente nos campos assim que ele chega.
  useEffect(() => {
    const e = exame.data;
    if (!e) return;
    setForm({
      dataExame: e.dataExame,
      pacienteId: e.pacienteId,
      procedimentoId: String(e.procedimentoId ?? ''),
      tipo: e.tipo,
      situacao: e.situacao,
      modoEnvio: String(e.modoEnvio ?? 'LAUDO_NO_SISTEMA'),
      empresaId: String(e.empresaId ?? ''),
      convenioId: String(e.convenioId ?? ''),
      medicoId: String(e.medicoId ?? ''),
      funcao: String(e.funcao ?? ''),
      medicinaOcupacional: e.medicinaOcupacional ? 'true' : 'false',
      medicoSolicitante: String(e.medicoSolicitante ?? ''),
      medicoSolicitanteCrm: String(e.medicoSolicitanteCrm ?? ''),
      medicoAcompanhamentoId: String(e.medicoAcompanhamentoId ?? ''),
      valorExame: paraCampoDinheiro(e.valorExame as number | null),
    });
    setPaciente({
      id: e.pacienteId,
      nome: e.pacienteNome,
      cpf: null,
      nascimento: String(e.pacienteNascimento ?? ''),
      matricula: e.pacienteMatricula,
    });
  }, [exame.data]);

  const recusado = exame.data?.situacao === 'RECUSADO';

  /* Os dois caminhos de volta de um exame recusado. A diferença entre eles é
     o que estava errado: o cadastro, ou a chapa. */
  const reenviar = useMutation({
    mutationFn: () => api.post(`/exames/${id}/reenviar-para-laudo`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exame', id] });
      void qc.invalidateQueries({ queryKey: ['exames'] });
      navegar('/fila');
    },
    onError: (e) => alert(e instanceof ErroApi ? e.message : 'Não foi possível reenviar.'),
  });

  const removerImagens = useMutation({
    mutationFn: () => api.post(`/exames/${id}/remover-imagens`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exame', id] });
      void qc.invalidateQueries({ queryKey: ['imagens', id] });
      void qc.invalidateQueries({ queryKey: ['exames'] });
    },
    onError: (e) => alert(e instanceof ErroApi ? e.message : 'Não foi possível remover as imagens.'),
  });

  /* Apagar UMA chapa. O "remover imagens" leva todas e devolve o exame à sala
     de RX -- serve para a chapa toda errada, não para a segunda ter saído
     tremida enquanto a primeira está boa. Ali, apagar as duas manda repetir um
     exame que já estava metade pronto. */
  const excluirImagem = useMutation({
    mutationFn: (imagemId: string) => api.apagar(`/imagens/${imagemId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['imagens', id] });
      void qc.invalidateQueries({ queryKey: ['exame', id] });
      void qc.invalidateQueries({ queryKey: ['exames'] });
    },
    onError: (e) => alert(e instanceof ErroApi ? e.message : 'Não foi possível excluir a imagem.'),
  });

  /* Corrigir um laudo já assinado. O médico escreveu lado trocado, medida
     errada, texto de outro paciente -- e sem isto a clínica ficaria entre
     deixar o erro no prontuário e cancelar o exame, que apaga o atendimento
     inteiro. A versão assinada não se perde: fica no histórico do laudo. */
  const reabrirLaudo = useMutation({
    mutationFn: (motivo: string) => api.post(`/exames/${id}/reabrir-laudo`, { motivo }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exame', id] });
      void qc.invalidateQueries({ queryKey: ['exames'] });
      navegar(`/laudos/${id}`);
    },
    onError: (e) =>
      alert(e instanceof ErroApi ? e.message : 'Não foi possível reabrir o laudo.'),
  });

  /* Quem levou só a imagem e depois quis o laudo. Sai daqui e não da fila,
     junto das outras ações do exame. */
  const mandarParaLaudo = useMutation({
    mutationFn: () => api.post(`/exames/${id}/mandar-para-laudo`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exame', id] });
      void qc.invalidateQueries({ queryKey: ['exames'] });
    },
    onError: (e) =>
      alert(e instanceof ErroApi ? e.message : 'Não foi possível mandar para o laudo.'),
  });

  /* Reescreve o rodapé queimado nas imagens. O texto vem do cadastro; o
     arquivo é refeito a partir do DICOM original guardado no servidor. */
  const regravar = useMutation({
    mutationFn: () => api.post<{ refeitas: number; sem: number; aviso: string | null }>(
      `/imagens/exame/${id}/regravar`, {}),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['imagens', id] });
      alert(r.aviso ?? `Identificação reescrita em ${r.refeitas} imagem(ns).`);
    },
    onError: (e) => alert(e instanceof ErroApi ? e.message : 'Não foi possível regravar.'),
  });

  const imagens = useQuery({
    queryKey: ['imagens', id],
    queryFn: ({ signal }) =>
      api.get<{
        itens: {
          id: string;
          ordem: number;
          titulo: string | null;
          larguraPx: number;
          alturaPx: number;
          temOriginal: boolean;
          dcmBytes: number;
          divergenciaCadastro: boolean;
          dicomPacienteNome: string | null;
        }[];
      }>(`/imagens/exame/${id}`, signal),
    enabled: !novo,
    // A imagem pode chegar do eFilm com a tela aberta.
    refetchInterval: 30_000,
  });

  // Exames anteriores do mesmo paciente: é o que evita lançar duas vezes o
  // mesmo pedido quando a pessoa volta ao balcão.
  const recentes = useQuery({
    queryKey: ['exames', 'recentes', paciente?.id],
    queryFn: ({ signal }) =>
      api.get<{ itens: ExameLinha[] }>(`/exames/paciente/${paciente!.id}/recentes`, signal),
    enabled: !!paciente?.id,
  });

  const procedimento = opcoes.data?.procedimentos.find((p) => String(p.id) === form.procedimentoId);
  const laudado = !!exame.data?.laudoAssinadoEm;
  const laudoLegado = exame.data?.laudoLegado === true;
  const temImagem = (exame.data?.imagens ?? 0) > 0;
  // Olha o que está gravado, não o que está no formulário: trocar o campo na
  // tela sem salvar não deveria mudar qual ação aparece no topo.
  const ehSemLaudo = exame.data?.modoEnvio === 'SEM_LAUDO';
  /* SEM_LAUDO e PORTAL entregam a mesma folha, e por isso andam juntos aqui.
     Mas só o SEM_LAUDO volta para o médico: no PORTAL o laudo sai por fora, e
     mandá-lo para a fila daqui contradiz o modo escolhido. É por isso que são
     duas variáveis e não uma. */
  const temDocumento = temDocumentoSemLaudo(exame.data?.modoEnvio);
  // Exame que a clínica não lauda e já saiu com a chapa: para o paciente ele
  // está tão pronto quanto um laudado. Mesma regra da consulta pública.
  const prontoSemLaudo = temDocumento && exame.data?.situacao === 'FINALIZADO' && temImagem;

  const salvar = useMutation({
    mutationFn: (dados: FormExame) =>
      novo ? api.post<ExameLinha>('/exames', dados) : api.put<ExameLinha>(`/exames/${id}`, dados),
    onSuccess: (e) => {
      void qc.invalidateQueries({ queryKey: ['exames'] });
      void qc.invalidateQueries({ queryKey: ['exame', id] });
      // Exame recusado: salvar é o mesmo gesto que devolver ao médico. Separar
      // em dois cliques deixaria o exame corrigido parado na lista de
      // recusados, esperando alguém lembrar de reenviar.
      if (recusado) {
        reenviar.mutate();
        return;
      }
      navegar('/fila', { state: { salvo: e.codigo } });
    },
    onError: (e) => {
      if (e instanceof ErroApi) {
        setCampos(e.campos);
        if (!e.temCampos) setErroGeral(e.message);
      } else {
        setErroGeral('Não foi possível salvar.');
      }
    },
  });

  const excluir = useMutation({
    mutationFn: () => api.apagar(`/exames/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exames'] });
      navegar('/fila');
    },
    onError: (e) => setErroGeral(e instanceof ErroApi ? e.message : 'Não foi possível excluir.'),
  });

  function enviar(ev: FormEvent) {
    ev.preventDefault();
    setCampos({});
    setErroGeral('');
    salvar.mutate({ ...form, pacienteId: paciente?.id ?? '' });
  }

  if (!novo && exame.isPending) {
    return (
      <Layout>
        <Cabecalho titulo="Exame" />
        <p className="vazio">Carregando…</p>
      </Layout>
    );
  }

  if (!novo && exame.error) {
    return (
      <Layout>
        <Cabecalho titulo="Exame" />
        <div className="cartao vazio">
          <p className="vazio__titulo">Não foi possível abrir o exame</p>
          <p>{exame.error instanceof ErroApi ? exame.error.message : 'Tente de novo.'}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* As ações do exame vivem aqui, no alto e à direita, e não em cada linha
          da fila: na lista elas ficavam depois de oito colunas, fora da tela em
          monitor comum, e obrigavam a rolar na horizontal para alcançar. */}
      <Cabecalho titulo={novo ? 'Novo exame' : `Exame ${exame.data?.codigo ?? ''}`}>
        {!novo && exame.data && temImagem && !laudado && ehSemLaudo && (
          <Botao
            carregando={mandarParaLaudo.isPending}
            onClick={() => {
              if (confirm(`Mandar o exame ${exame.data!.codigo} para o médico laudar?`)) {
                mandarParaLaudo.mutate();
              }
            }}
          >
            Mandar p/ laudo
          </Botao>
        )}
        {/* Ver o laudo pronto e escrever um laudo novo dividiam a mesma condição,
            e isso escondia o laudo dos 33 mil exames migrados do WX3: eles vieram
            com o PDF mas sem imagem no sistema, então `temImagem` era falso e o
            botão nunca aparecia -- não havia como abrir o laudo pela tela.

            São coisas diferentes. Laudar exige a chapa, porque ninguém lauda no
            escuro; ver o que já está assinado não exige nada além de existir. */}
        {!novo && exame.data && laudado && (
          <Botao variante="neutro" onClick={() => navegar(`/laudos/${id}`)}>
            Ver laudo
          </Botao>
        )}
        {!novo && exame.data && !laudado && temImagem && !temDocumento && (
          <Botao variante="primario" onClick={() => navegar(`/laudos/${id}`)}>
            Laudar
          </Botao>
        )}
        <Botao variante="neutro" onClick={() => navegar('/fila')}>
          Voltar para a fila
        </Botao>
      </Cabecalho>

      <div className="exame">
        <form className="exame__forma cartao" onSubmit={enviar} noValidate>
          {!novo && exame.data && (
            <header className="exame__estado">
              <Chapa codigo={exame.data.codigo} estagio={exame.data.situacao} />
              <Selo estagio={exame.data.situacao} />
              {exame.data.imagens > 0 && (
                <span className="exame__nota">
                  {exame.data.imagens} imagem(ns) recebida(s) do eFilm
                </span>
              )}
            </header>
          )}

          <div className="exame__campos grade">
            {erroGeral && (
              <div className="campo campo--total">
                <Aviso>{erroGeral}</Aviso>
              </div>
            )}
            {recusado && exame.data?.recusaMotivo && (
              <div className="campo campo--total">
                <div className="exame__recusa">
                  <p className="exame__recusa-titulo">O médico devolveu este exame</p>
                  <p className="exame__recusa-motivo">{exame.data.recusaMotivo}</p>
                  <p className="exame__recusa-apoio">
                    Corrija o que for preciso e use <strong>Salvar e reenviar</strong> para
                    devolvê-lo ao médico. Se o problema for a radiografia,
                    <strong> Remover imagens</strong> manda o exame de volta para a sala de RX.
                  </p>
                </div>
              </div>
            )}
            {laudado && (
              <div className="campo campo--total">
                <Aviso tipo="info">
                  Este exame já foi laudado. O cadastro fica bloqueado; correções são feitas
                  gerando uma nova versão do laudo.
                </Aviso>
              </div>
            )}

            <CamposDoExame
              form={form}
              campos={campos}
              opcoes={opcoes.data}
              paciente={paciente}
              aoMudar={(m) => setForm((f) => ({ ...f, ...m }))}
              aoEscolherPaciente={setPaciente}
              bloqueado={laudado}
              mostrarSituacao={!novo}
              mostrarValores={podeVerValores}
            />

            {!laudado && (
              <div className="exame__acoes campo--total">
                {/* Exame recusado tem outro par de ações: salvar aqui não é
                    "guardar e sair", é devolver o exame para o médico. */}
                <Botao
                  type="submit"
                  variante="primario"
                  carregando={salvar.isPending || reenviar.isPending}
                >
                  {recusado
                    ? 'Salvar e reenviar ao médico'
                    : salvar.isPending
                      ? 'Salvando…'
                      : novo
                        ? 'Cadastrar exame'
                        : 'Salvar alterações'}
                </Botao>
                {recusado && (
                  <Botao
                    type="button"
                    variante="perigo"
                    carregando={removerImagens.isPending}
                    onClick={() => {
                      if (
                        confirm(
                          `Remover as imagens do exame ${exame.data?.codigo}?

` +
                            'As radiografias e o arquivo DICOM são apagados, e o exame volta ' +
                            'para "aguardando exame". O paciente terá de repetir a radiografia.',
                        )
                      ) {
                        removerImagens.mutate();
                      }
                    }}
                  >
                    Remover imagens
                  </Botao>
                )}
                <Botao type="button" variante="silencioso" onClick={() => navegar('/fila')}>
                  Cancelar
                </Botao>
                {!novo && (
                  <Botao
                    type="button"
                    variante="perigo"
                    className="exame__excluir"
                    carregando={excluir.isPending}
                    onClick={() => {
                      if (confirm(`Excluir o exame ${exame.data?.codigo}?`)) excluir.mutate();
                    }}
                  >
                    Excluir
                  </Botao>
                )}
              </div>
            )}
          </div>
        </form>

        <aside className="exame__lado">
          {/* Só quando existe documento para o paciente pegar: antes disso o
              link levaria a uma tela dizendo que ainda não está pronto. E só
              para quem tem a permissão -- falar com o paciente em nome da
              clínica não é consequência de poder abrir o exame.

              São dois caminhos, não um. O laudo assinado é o comum. O outro é o
              exame que a clínica não lauda: o paciente pediu só a chapa, para
              levar ao médico dele, e ele fica pronto ao ser finalizado. Esse
              vinha caindo fora daqui por depender de `laudado`, e a recepção
              tinha de avisar por fora do sistema -- sem o texto padrão e sem o
              registro de que avisou. */}
          {!novo && (laudado || prontoSemLaudo) && podeAvisarPaciente && (
            <AvisarWhatsapp exameId={id!} />
          )}

          {/* Exame que veio do WX3. O laudo dele é um PDF pronto, com as chapas
              dentro, e não há folha para abrir na tela -- então o caminho útil
              aqui é baixar, e não navegar para um editor sem o que editar. */}
          {!novo && laudoLegado && (
            <div className="cartao exame__importado">
              <h2 className="painel__titulo">Exame importado</h2>
              <p className="campo__dica">
                Veio do sistema antigo. O laudo é um PDF pronto, com as radiografias
                dentro do próprio arquivo.
              </p>
              <Botao
                variante="primario"
                onClick={() => window.open(`/api/laudos/exame/${id}/pdf`, '_blank')}
              >
                Baixar laudo
              </Botao>
            </div>
          )}

          {/* Fica aqui, e não junto das ações do topo: desfazer uma assinatura
              médica não é gesto de rotina, e ao lado de "Ver laudo" ficava a um
              clique do que se faz o dia inteiro. Aqui o botão vem depois do que
              vai acontecer, escrito. */}
          {!novo && laudado && usuario.admin && (
            <div className="cartao exame__reabrir">
              <h2 className="painel__titulo">Corrigir o laudo</h2>
              <p className="campo__dica">
                O laudo volta para o médico e o PDF atual deixa de valer. A versão assinada
                fica guardada no histórico do exame.
              </p>
              <Botao
                className="exame__reabrir-botao"
                carregando={reabrirLaudo.isPending}
                onClick={() => {
                  const motivo = prompt(
                    `Reabrir o laudo do exame ${exame.data!.codigo} para correção.

` +
                      'Escreva o motivo:',
                  );
                  if (motivo?.trim()) reabrirLaudo.mutate(motivo.trim());
                }}
              >
                Reabrir laudo
              </Botao>
            </div>
          )}

          {/* Exame que a clínica não lauda: o paciente leva a imagem ao médico
              dele. Sem este documento ele sai do balcão com um envelope em que
              nada diz de quem é a chapa nem que exame é aquele. */}
          {!novo && temDocumento && (imagens.data?.itens.length ?? 0) > 0 && (
            <div className="cartao exame__importado">
              <h2 className="painel__titulo">Documento do exame</h2>
              <p className="campo__dica">
                Cabeçalho da clínica, os dados do paciente e as imagens. Não é laudo, e o
                próprio documento diz isso.
              </p>
              <Botao
                variante="primario"
                onClick={() => window.open(`/api/exames/${id}/documento`, '_blank')}
              >
                Gerar documento
              </Botao>
            </div>
          )}

          {!novo && (imagens.data?.itens.length ?? 0) > 0 && (
            <div className="cartao exame__imagens">
              <header className="exame__recentes-topo">
                <h2 className="painel__titulo">
                  Imagens ({imagens.data?.itens.length})
                </h2>
                {/* A faixa de identificação fica queimada no arquivo, então
                    mudar o cadastro não muda a imagem: quem corrige o nome, a
                    data ou o procedimento precisa mandar reescrever. */}
                {!laudado && podeAlterar && (imagens.data?.itens.some((i) => i.temOriginal) ?? false) && (
                  <Botao
                    type="button"
                    tamanho="sm"
                    carregando={regravar.isPending}
                    onClick={() => {
                      if (confirm('Reescrever a identificação gravada nas imagens com os dados atuais do cadastro?')) {
                        regravar.mutate();
                      }
                    }}
                  >
                    Regravar identificação
                  </Botao>
                )}
              </header>
              <div className="chapas">
                {imagens.data?.itens.map((im, i) => (
                  <figure key={im.id} className="chapa-img">
                    <a
                      href={`/api/imagens/${im.id}/laudo`}
                      target="_blank"
                      rel="noreferrer"
                      title="Abrir em tamanho de laudo"
                    >
                      <img src={`/api/imagens/${im.id}/web`} alt="Radiografia" />
                    </a>
                    <figcaption>
                      <span className="chapa-img__titulo">Imagem {i + 1}</span>
                      <span className="chapa-img__apoio mono">
                        {im.larguraPx}×{im.alturaPx}
                      </span>
                      {im.divergenciaCadastro && (
                        <span className="chapa-img__alerta" title={`No arquivo: ${im.dicomPacienteNome}`}>
                          Nome diverge do cadastro
                        </span>
                      )}
                      {im.temOriginal ? (
                        <a className="chapa-img__original" href={`/api/imagens/${im.id}/original`}>
                          Baixar DICOM ({(im.dcmBytes / 1e6).toFixed(1)} MB)
                        </a>
                      ) : (
                        /* Não é espera: o .dcm não sobe, por decisão da clínica.
                           Dizer "ainda subindo" prometia uma chegada que nunca
                           acontece, e quem precisasse do original ficaria
                           aguardando em vez de ir buscá-lo onde ele está. */
                        <span
                          className="chapa-img__apoio"
                          title="O arquivo DICOM não é enviado ao servidor: ele fica guardado no computador da sala de RX."
                        >
                          Original na sala de RX
                        </span>
                      )}
                      {/* Laudado não aparece: a imagem faz parte do documento
                          assinado, e apagá-la deixaria um laudo descrevendo uma
                          radiografia que não existe mais. A API recusa de
                          qualquer jeito -- aqui é só para não oferecer. */}
                      {!laudado && podeExcluirImagem && (
                        <Botao
                          type="button"
                          tamanho="sm"
                          variante="perigo"
                          className="chapa-img__excluir"
                          carregando={excluirImagem.isPending}
                          onClick={() => {
                            const ultima = (imagens.data?.itens.length ?? 0) === 1;
                            if (
                              confirm(
                                `Excluir a imagem ${i + 1} do exame ${exame.data?.codigo}?\n\n` +
                                  'Isto não tem volta: a radiografia é apagada do sistema.\n' +
                                  'O arquivo DICOM original continua na pasta de processados ' +
                                  'do computador da sala de RX.' +
                                  (ultima
                                    ? '\n\nÉ a última imagem: o exame volta para "aguardando exame".'
                                    : ''),
                              )
                            ) {
                              excluirImagem.mutate(im.id);
                            }
                          }}
                        >
                          Excluir
                        </Botao>
                      )}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          )}

          {novo && (
            <div className="cartao exame__aviso-codigo">
              <p className="rotulo">Código do exame</p>
              <p className="exame__explica">
                O código é gerado ao salvar. É ele que o técnico digita no eFilm para a imagem
                encontrar este exame.
              </p>
            </div>
          )}

          <div className="cartao exame__recentes">
            <header className="exame__recentes-topo">
              <h2 className="painel__titulo">Exames deste paciente</h2>
            </header>
            {!paciente ? (
              <p className="vazio">Escolha o paciente para ver o histórico.</p>
            ) : recentes.isPending ? (
              <p className="vazio">Carregando…</p>
            ) : (recentes.data?.itens.length ?? 0) === 0 ? (
              <div className="vazio">
                <p className="vazio__titulo">Primeiro exame</p>
                <p>Este paciente ainda não tem exames registrados.</p>
              </div>
            ) : (
              <ul className="exame__historico">
                {recentes.data?.itens
                  .filter((e) => e.id !== id)
                  .map((e) => (
                    <li key={e.id}>
                      <button
                        type="button"
                        className="exame__historico-item"
                        onClick={() => navegar(`/exames/${e.id}`)}
                      >
                        <span className="exame__historico-topo">
                          <Chapa codigo={e.codigo} estagio={e.situacao} />
                          <span className="mono">{dataBr(e.dataExame)}</span>
                        </span>
                        <span className="exame__historico-nome">{e.procedimentoNome}</span>
                        <Selo estagio={e.situacao} />
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {podeVerValores && procedimento && (
            <div className="cartao exame__tabela">
              <p className="rotulo">Tabela do procedimento</p>
              <p className="exame__explica">
                Cobrado <strong className="mono">{dinheiro(procedimento.valor)}</strong> · médico{' '}
                <strong className="mono">{dinheiro(procedimento.valorMedico)}</strong>
              </p>
            </div>
          )}
        </aside>
      </div>
    </Layout>
  );
}
